// Workstream: accounts. REST client for /api/account and /api/images.
import type { PackSummary } from '@shared/types';
import { ApiError } from '../../lib/api';

export interface AccountUser {
  id: number;
  email: string;
  displayName: string;
}

export interface HistoryEntry {
  gameId: number;
  playedAt: string;
  playerName: string;
  score: number;
  won: boolean;
  playerCount: number;
  rounds: number;
  winnerName: string | null;
  roomCode: string;
}

export interface History {
  games: HistoryEntry[];
  totals: { games: number; wins: number; points: number };
}

export interface ClaimResult {
  claimed: string[];
  alreadyYours: string[];
  skipped: string[];
}

export interface UploadedImage {
  id: string;
  url: string;
}

async function request<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (init.json !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers,
      credentials: 'same-origin',
      body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
    });
  } catch {
    throw new ApiError(0, 'Could not reach the server. Check your connection.');
  }
  const body = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) throw new ApiError(res.status, (body as { error?: string })?.error ?? `Request failed (${res.status}).`);
  return body as T;
}

export const accountApi = {
  me: () => request<{ user: AccountUser | null }>('/account/me'),
  signup: (email: string, password: string, displayName: string) =>
    request<{ user: AccountUser }>('/account/signup', { method: 'POST', json: { email, password, displayName } }),
  login: (email: string, password: string) => request<{ user: AccountUser }>('/account/login', { method: 'POST', json: { email, password } }),
  logout: () => request<{ ok: true }>('/account/logout', { method: 'POST', json: {} }),
  rename: (displayName: string) => request<{ user: AccountUser }>('/account/me', { method: 'PATCH', json: { displayName } }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/account/password', { method: 'POST', json: { currentPassword, newPassword } }),
  packs: () => request<PackSummary[]>('/account/packs'),
  claim: (packs: { code: string; editKey: string }[]) => request<ClaimResult>('/account/claim', { method: 'POST', json: { packs } }),
  history: () => request<History>('/account/history'),
  /** Signed-in users need nothing extra; guests prove they can edit a pack with its code and key. */
  uploadImage: (blob: Blob, pack?: { code: string; editKey: string }) =>
    request<UploadedImage>('/images', {
      method: 'POST',
      body: blob,
      headers: {
        'content-type': blob.type || 'image/jpeg',
        ...(pack?.editKey ? { 'x-pack-code': pack.code, 'x-edit-key': pack.editKey } : {}),
      },
    }),
};
